// Gestionnaire audio - musique procédurale + SFX
// Tout est généré via Web Audio API (fiable, pas de network dependency)

let audioCtx = null;
let musicScheduler = null;
let musicNodes = [];
let currentTrack = null;
let musicVolume = 0.25;
let sfxVolume = 0.4;
let muted = false;

const loadSettings = () => {
  try {
    const saved = JSON.parse(localStorage.getItem("checkpoint_audio") || "{}");
    if (typeof saved.musicVolume === "number") musicVolume = saved.musicVolume;
    if (typeof saved.sfxVolume === "number") sfxVolume = saved.sfxVolume;
    if (typeof saved.muted === "boolean") muted = saved.muted;
  } catch (e) {}
};
loadSettings();

const saveSettings = () => {
  localStorage.setItem("checkpoint_audio", JSON.stringify({ musicVolume, sfxVolume, muted }));
};

const ensureCtx = () => {
  if (!audioCtx) {
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) { return null; }
  }
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
};

// ============ SFX procéduraux ============
const playTone = ({ freq = 440, duration = 0.1, type = "sine", gain = 0.3, freqEnd = null, delay = 0 }) => {
  if (muted) return;
  const ctx = ensureCtx();
  if (!ctx) return;
  const now = ctx.currentTime + delay;
  // Garde-fou : si l'AudioContext est suspendu (autoplay policy), currentTime peut être non-fini.
  // On ne doit JAMAIS laisser une exception audio interrompre le handler appelant (combat, clics…).
  if (!Number.isFinite(now) || !Number.isFinite(freq) || !Number.isFinite(gain)) return;
  try {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, now);
    if (freqEnd !== null && Number.isFinite(freqEnd)) osc.frequency.exponentialRampToValueAtTime(Math.max(1, freqEnd), now + duration);
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(gain * sfxVolume, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + duration + 0.05);
  } catch (e) { /* audio non disponible : ignorer silencieusement */ }
};

const playNoise = ({ duration = 0.1, gain = 0.2, filterFreq = 1000, delay = 0 }) => {
  if (muted) return;
  const ctx = ensureCtx();
  if (!ctx) return;
  const now = ctx.currentTime + delay;
  if (!Number.isFinite(now) || !Number.isFinite(gain)) return;
  try {
    const bufferSize = Math.floor(ctx.sampleRate * duration);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain * sfxVolume, now);
    g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = filterFreq;
    src.connect(filter);
    filter.connect(g);
    g.connect(ctx.destination);
    src.start(now);
    src.stop(now + duration + 0.02);
  } catch (e) { /* audio non disponible : ignorer silencieusement */ }
};

export const SFX = {
  click: () => { playTone({ freq: 800, duration: 0.05, type: "square", gain: 0.15 }); playTone({ freq: 1200, duration: 0.04, type: "square", gain: 0.1, delay: 0.02 }); },
  hover: () => playTone({ freq: 600, duration: 0.04, type: "sine", gain: 0.08 }),
  attack: () => { playTone({ freq: 200, duration: 0.08, type: "sawtooth", gain: 0.25, freqEnd: 60 }); playNoise({ duration: 0.1, gain: 0.18, filterFreq: 1500, delay: 0.02 }); },
  hit: () => { playNoise({ duration: 0.12, gain: 0.22, filterFreq: 600 }); playTone({ freq: 150, duration: 0.1, type: "square", gain: 0.15 }); },
  crit: () => { playTone({ freq: 800, duration: 0.06, type: "square", gain: 0.2 }); playTone({ freq: 1200, duration: 0.08, type: "sawtooth", gain: 0.25, delay: 0.04 }); playTone({ freq: 1600, duration: 0.1, type: "square", gain: 0.2, delay: 0.08 }); },
  fire: () => { playNoise({ duration: 0.4, gain: 0.18, filterFreq: 400 }); playTone({ freq: 80, duration: 0.3, type: "sawtooth", gain: 0.15, freqEnd: 40 }); },
  victory: () => { [523, 659, 784, 1047].forEach((f, i) => playTone({ freq: f, duration: 0.15, type: "square", gain: 0.25, delay: i * 0.1 })); },
  defeat: () => { [400, 300, 200, 100].forEach((f, i) => playTone({ freq: f, duration: 0.2, type: "sawtooth", gain: 0.25, delay: i * 0.15 })); },
  coin: () => { playTone({ freq: 988, duration: 0.06, type: "square", gain: 0.2 }); playTone({ freq: 1319, duration: 0.1, type: "square", gain: 0.18, delay: 0.05 }); },
  chest: () => { playTone({ freq: 440, duration: 0.1, type: "triangle", gain: 0.2 }); playTone({ freq: 660, duration: 0.15, type: "triangle", gain: 0.2, delay: 0.1 }); playTone({ freq: 880, duration: 0.2, type: "triangle", gain: 0.2, delay: 0.2 }); },
  shield: () => { playTone({ freq: 300, duration: 0.15, type: "triangle", gain: 0.2 }); playNoise({ duration: 0.1, gain: 0.1, filterFreq: 200, delay: 0.05 }); },
  heal: () => { [440, 587, 740].forEach((f, i) => playTone({ freq: f, duration: 0.15, type: "sine", gain: 0.2, delay: i * 0.08 })); },
  freeze: () => { playTone({ freq: 1200, duration: 0.3, type: "sine", gain: 0.15, freqEnd: 800 }); playNoise({ duration: 0.2, gain: 0.08, filterFreq: 3000, delay: 0.05 }); },
  stun: () => { playTone({ freq: 600, duration: 0.1, type: "square", gain: 0.2 }); playTone({ freq: 800, duration: 0.1, type: "square", gain: 0.2, delay: 0.1 }); playTone({ freq: 1000, duration: 0.1, type: "square", gain: 0.2, delay: 0.2 }); },
  miss: () => playTone({ freq: 300, duration: 0.1, type: "sawtooth", gain: 0.12, freqEnd: 200 }),
  notify: () => { playTone({ freq: 880, duration: 0.08, type: "triangle", gain: 0.2 }); playTone({ freq: 1320, duration: 0.12, type: "triangle", gain: 0.2, delay: 0.06 }); },
  // Iso mode
  step: () => { playNoise({ duration: 0.04, gain: 0.06, filterFreq: 400 }); playTone({ freq: 90, duration: 0.04, type: "sine", gain: 0.04 }); },
  door: () => { playTone({ freq: 220, duration: 0.18, type: "triangle", gain: 0.18, freqEnd: 440 }); playNoise({ duration: 0.12, gain: 0.1, filterFreq: 800, delay: 0.05 }); },
  chest: () => { playTone({ freq: 330, duration: 0.1, type: "triangle", gain: 0.18 }); playTone({ freq: 660, duration: 0.18, type: "triangle", gain: 0.2, delay: 0.07 }); playTone({ freq: 990, duration: 0.22, type: "triangle", gain: 0.18, delay: 0.16 }); },
  buy: () => { playTone({ freq: 800, duration: 0.07, type: "square", gain: 0.18 }); playTone({ freq: 1200, duration: 0.1, type: "square", gain: 0.16, delay: 0.05 }); playTone({ freq: 1500, duration: 0.08, type: "triangle", gain: 0.14, delay: 0.12 }); },
  // Boss : charge signature (montée de tension) + libération (déflagration)
  charge: () => { playTone({ freq: 110, duration: 1.1, type: "sawtooth", gain: 0.14, freqEnd: 520 }); playTone({ freq: 220, duration: 1.1, type: "triangle", gain: 0.08, freqEnd: 880, delay: 0.05 }); playNoise({ duration: 1.0, gain: 0.05, filterFreq: 600 }); },
  bossRelease: () => { playNoise({ duration: 0.5, gain: 0.28, filterFreq: 500 }); playTone({ freq: 160, duration: 0.45, type: "sawtooth", gain: 0.25, freqEnd: 50 }); playTone({ freq: 90, duration: 0.5, type: "square", gain: 0.18, freqEnd: 40, delay: 0.03 }); },
};

// ============ MUSIQUE PROCÉDURALE ============
// Notes en Hz : indices = degrés de gamme
const SCALE_AM    = [220.00, 246.94, 261.63, 293.66, 329.63, 349.23, 392.00, 440.00]; // A natural minor
const SCALE_DM    = [293.66, 329.63, 349.23, 392.00, 440.00, 466.16, 523.25, 587.33]; // D minor
const SCALE_EM    = [329.63, 369.99, 392.00, 440.00, 493.88, 523.25, 587.33, 659.25]; // E minor
const SCALE_GMAJ  = [196.00, 220.00, 246.94, 261.63, 293.66, 329.63, 369.99, 392.00]; // G major (heroic)
const SCALE_CMIN  = [261.63, 293.66, 311.13, 349.23, 392.00, 415.30, 466.16, 523.25]; // C minor (dark)
const SCALE_FDIM  = [349.23, 369.99, 415.30, 466.16, 493.88, 554.37, 622.25, 659.25]; // F diminished-ish (eerie)
const SCALE_BBMAJ = [233.08, 261.63, 293.66, 311.13, 349.23, 392.00, 440.00, 466.16]; // Bb major (regal)

const THEMES = {
  menu: {
    scale: SCALE_AM, tempo: 75,
    waveLead: "triangle", waveBass: "sine",
    leadGain: 0.18, bassGain: 0.22,
    melody: [0, 2, 4, 5, 7, 5, 4, 2, 0, null, 4, 7, 5, 4, 2, null],
    bass: [0, null, 3, null, 5, null, 4, null],
  },
  select: {
    scale: SCALE_DM, tempo: 85,
    waveLead: "triangle", waveBass: "sine",
    leadGain: 0.16, bassGain: 0.2,
    melody: [0, 2, 4, 7, 4, 2, null, 0, 4, 5, 4, 2, null, 0, 2, null],
    bass: [0, null, 4, null, 5, null, 3, null],
  },
  map: {
    scale: SCALE_AM, tempo: 65,
    waveLead: "sine", waveBass: "triangle",
    leadGain: 0.14, bassGain: 0.2,
    melody: [0, null, 2, null, 4, null, 5, null, 7, null, 5, null, 4, null, 2, null],
    bass: [0, null, null, null, 4, null, null, null],
  },
  combat: {
    scale: SCALE_EM, tempo: 130,
    waveLead: "sawtooth", waveBass: "square",
    leadGain: 0.14, bassGain: 0.16,
    melody: [0, 2, 0, 4, 0, 2, 3, 4, 5, 4, 3, 2, 0, 2, 4, 5],
    bass: [0, 0, 4, 4, 5, 5, 3, 3],
  },
  // === Nouveaux thèmes (variantes par étage / contexte) ===
  forest: {
    scale: SCALE_GMAJ, tempo: 90,
    waveLead: "triangle", waveBass: "sine",
    leadGain: 0.18, bassGain: 0.20,
    melody: [0, 2, 4, 2, 5, 4, 2, 0, 4, 5, 7, 5, 4, 2, 0, null],
    bass: [0, null, 4, null, 5, null, 4, null],
  },
  cavern: {
    scale: SCALE_CMIN, tempo: 55,
    waveLead: "sine", waveBass: "sine",
    leadGain: 0.10, bassGain: 0.30,
    melody: [0, null, null, 3, null, null, 0, null, 5, null, null, 4, null, null, 3, null],
    bass: [0, null, null, null, 4, null, null, null],
  },
  ruins: {
    scale: SCALE_DM, tempo: 75,
    waveLead: "triangle", waveBass: "triangle",
    leadGain: 0.16, bassGain: 0.22,
    melody: [0, 3, 5, 3, 0, 4, 3, 5, 7, 5, 4, 3, 2, 3, 0, null],
    bass: [0, null, 3, null, 5, null, 4, null],
  },
  glacial: {
    scale: SCALE_DM, tempo: 48,
    waveLead: "sine", waveBass: "sine",
    leadGain: 0.18, bassGain: 0.22,
    melody: [0, null, null, 4, null, null, 7, null, 5, null, null, 4, null, null, 2, null],
    bass: [0, null, null, null, 5, null, null, null],
  },
  volcano: {
    scale: SCALE_EM, tempo: 160,
    waveLead: "sawtooth", waveBass: "square",
    leadGain: 0.16, bassGain: 0.20,
    melody: [0, 0, 3, 2, 0, 3, 5, 4, 3, 0, 4, 7, 5, 3, 0, null],
    bass: [0, 0, 0, 0, 5, 5, 4, 4],
  },
  celestial: {
    scale: SCALE_GMAJ, tempo: 80,
    waveLead: "triangle", waveBass: "triangle",
    leadGain: 0.20, bassGain: 0.18,
    melody: [0, 2, 4, 7, 5, 4, 2, 0, 4, 7, 9, 7, 5, 4, 2, null],
    bass: [0, null, 4, null, 5, null, 4, null],
  },
  astral: {
    scale: SCALE_AM, tempo: 100,
    waveLead: "sine", waveBass: "triangle",
    leadGain: 0.14, bassGain: 0.22,
    melody: [0, 4, 7, 5, 4, 7, 9, 5, 4, null, 7, 9, 5, 0, 4, null],
    bass: [0, null, 5, null, 4, null, 3, null],
  },
  abyss: {
    scale: SCALE_CMIN, tempo: 42,
    waveLead: "square", waveBass: "sine",
    leadGain: 0.08, bassGain: 0.32,
    melody: [0, null, null, null, 3, null, null, null, 0, null, null, null, 5, null, null, null],
    bass: [0, null, null, null, 0, null, null, null],
  },
  boss: {
    scale: SCALE_EM, tempo: 155,
    waveLead: "sawtooth", waveBass: "square",
    leadGain: 0.15, bassGain: 0.2,
    melody: [0, 4, 7, 5, 7, 4, 0, 4, 7, 5, 4, 3, 0, 2, 4, 7],
    bass: [0, 0, 4, 4, 7, 7, 5, 5],
  },
  pvp: {
    scale: SCALE_BBMAJ, tempo: 140,
    waveLead: "sawtooth", waveBass: "square",
    leadGain: 0.15, bassGain: 0.2,
    melody: [0, 2, 4, 5, 4, 2, 0, 4, 7, 5, 4, 2, 0, 2, 4, 7],
    bass: [0, 0, 4, 4, 5, 5, 3, 3],
  },
  shop: {
    scale: SCALE_GMAJ, tempo: 95,
    waveLead: "sine", waveBass: "triangle",
    leadGain: 0.16, bassGain: 0.2,
    melody: [0, 2, 4, 2, 0, 4, 2, 0, null, 4, 5, 4, 2, 0, 2, null],
    bass: [0, null, 4, null, 5, null, 4, null],
  },
};

// Map a floor number (1-infinity) to a thematic music track.
// Aligned with /utils/themes.js FLOOR_THEMES rotation.
const FLOOR_TRACK_ROTATION = ["forest", "cavern", "ruins", "glacial", "volcano", "celestial", "astral", "abyss"];
export const trackForFloor = (floor = 1) => FLOOR_TRACK_ROTATION[(Math.max(1, floor) - 1) % FLOOR_TRACK_ROTATION.length];

const stopMusicScheduler = () => {
  if (musicScheduler) {
    clearInterval(musicScheduler);
    musicScheduler = null;
  }
  // Let scheduled notes finish naturally
  musicNodes = [];
};

const playMusicNote = (freq, duration, type, gain) => {
  const ctx = ensureCtx();
  if (!ctx || muted) return;
  const now = ctx.currentTime;
  if (!Number.isFinite(now) || !Number.isFinite(freq) || !Number.isFinite(gain)) return;
  try {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, now);
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(gain * musicVolume, now + 0.03);
    g.gain.linearRampToValueAtTime(gain * musicVolume * 0.7, now + duration * 0.7);
    g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 3000;
    osc.connect(filter);
    filter.connect(g);
    g.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + duration + 0.05);
  } catch (e) { /* audio non disponible : ignorer silencieusement */ }
};

export const playMusic = (themeName) => {
  if (currentTrack === themeName && musicScheduler) return;
  stopMusicScheduler();
  currentTrack = themeName;
  const theme = THEMES[themeName];
  if (!theme) return;

  // Wait for user gesture if context suspended
  const ctx = ensureCtx();
  if (!ctx) return;

  const noteDurationMs = (60 / theme.tempo) * 1000 / 2; // double tempo
  let beat = 0;

  const tick = () => {
    if (currentTrack !== themeName) return;
    const melodyIdx = theme.melody[beat % theme.melody.length];
    if (melodyIdx !== null && melodyIdx !== undefined) {
      const freq = theme.scale[melodyIdx];
      playMusicNote(freq, noteDurationMs / 1000 * 0.9, theme.waveLead, theme.leadGain);
    }
    // Bass
    if (beat % 2 === 0) {
      const bassIdx = theme.bass[(beat / 2) % theme.bass.length];
      if (bassIdx !== null && bassIdx !== undefined) {
        const bassFreq = theme.scale[bassIdx] / 2;
        playMusicNote(bassFreq, (noteDurationMs / 1000) * 1.8, theme.waveBass, theme.bassGain);
      }
    }
    beat++;
  };

  // Play first beat immediately, then schedule
  tick();
  musicScheduler = setInterval(tick, noteDurationMs);
};

export const stopMusic = () => {
  stopMusicScheduler();
  currentTrack = null;
};

export const setMusicVolume = (vol) => {
  musicVolume = Math.max(0, Math.min(1, vol));
  saveSettings();
};

export const setSfxVolume = (vol) => {
  sfxVolume = Math.max(0, Math.min(1, vol));
  saveSettings();
};

export const setMuted = (m) => {
  muted = m;
  if (m) stopMusicScheduler();
  else if (currentTrack) {
    const t = currentTrack;
    currentTrack = null;
    playMusic(t);
  }
  saveSettings();
};

export const getAudioSettings = () => ({ musicVolume, sfxVolume, muted });

// Auto-resume audio context on first user gesture
let resumed = false;
const resume = () => {
  if (resumed) return;
  resumed = true;
  ensureCtx();
  if (currentTrack && !musicScheduler) {
    const t = currentTrack;
    currentTrack = null;
    playMusic(t);
  }
};
if (typeof window !== "undefined") {
  ["click", "keydown", "touchstart"].forEach((evt) => {
    window.addEventListener(evt, resume, { once: false });
  });
}
